import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Star, Languages, Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useApp } from "@/context/AppContext";
import { getAllReviews } from "@/lib/api";
import { tr } from "@/i18n/translations";

export default function HotelReviews() {
  const { slug } = useParams();
  const { t, lang } = useApp();
  const [data, setData] = useState(null);
  const [sort, setSort] = useState("relevant");
  const [onlyCons, setOnlyCons] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getAllReviews(slug, lang, sort, onlyCons)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug, lang, sort, onlyCons]);

  useEffect(() => { load(); }, [load]);

  const sorts = [
    { id: "relevant", label: t("detail.sort_relevant") },
    { id: "high", label: t("detail.sort_high") },
    { id: "low", label: t("detail.sort_low") },
  ];

  return (
    <div className="min-h-screen bg-sand">
      <Navbar solid />
      <div className="mx-auto max-w-3xl px-5 py-24">
        {data && (
          <>
            <Link to={`/stay/${slug}`} data-testid="back-to-hotel" className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-terracotta hover:underline">
              <ArrowLeft size={16} /> {data.hotel}
            </Link>
            <div className="flex items-center gap-5 rounded-3xl border border-sand-border bg-sand-card p-6">
              {data.image && <img src={data.image} alt={data.hotel} className="h-20 w-24 rounded-2xl object-cover" />}
              <div>
                <h1 className="font-serif text-2xl font-semibold text-petrol">{t("detail.reviews")}</h1>
                <div className="mt-1 flex items-center gap-2 text-sm text-petrol/70">
                  {data.rating && <span className="rounded-md bg-emerald-600 px-2 py-0.5 text-sm font-bold text-white">{data.rating}</span>}
                  {data.review_word && <span className="font-semibold">{data.review_word}</span>}
                  <span>· {data.reviews_count} · {t("detail.reviews_source")}</span>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              {sorts.map((s) => (
                <button
                  key={s.id}
                  data-testid={`review-sort-${s.id}`}
                  onClick={() => setSort(s.id)}
                  className={`rounded-none px-4 py-2 text-sm font-medium transition-colors ${sort === s.id ? "bg-petrol text-sand" : "border border-sand-border bg-sand-card text-petrol hover:border-terracotta"}`}
                >
                  {s.label}
                </button>
              ))}
              <button
                data-testid="review-filter-cons"
                onClick={() => setOnlyCons((v) => !v)}
                className={`rounded-none px-4 py-2 text-sm font-medium transition-colors ${onlyCons ? "bg-terracotta text-white" : "border border-sand-border bg-sand-card text-petrol hover:border-terracotta"}`}
              >
                {t("detail.only_criticism")}
              </button>
            </div>

            {data.auto_translated && (
              <span className="mt-4 inline-flex items-center gap-1.5 rounded-none bg-sage/15 px-2.5 py-1 text-xs font-medium text-sage-dark">
                <Languages size={12} /> {t("detail.auto_translated")}
              </span>
            )}

            {loading ? (
              <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-terracotta" /></div>
            ) : (
              <div className="mt-6 flex flex-col gap-4" data-testid="all-reviews-list">
                {data.reviews.length === 0 && <p className="text-petrol/60">{t("detail.no_reviews")}</p>}
                {data.reviews.map((r, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.03 }}
                    className="rounded-2xl border border-sand-border bg-sand-card p-5"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-none bg-petrol font-serif text-lg text-sand">{r.author[0]}</div>
                        <div>
                          <p className="font-semibold text-petrol">{r.author}</p>
                          <p className="text-xs text-petrol/50">{r.country} · {r.date}</p>
                        </div>
                      </div>
                      {r.score10 && (
                        <span className="flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-0.5 text-sm font-bold text-white">
                          <Star size={11} className="fill-white" /> {r.score10}
                        </span>
                      )}
                    </div>
                    <p className="mt-3 text-petrol/75">{tr(r.text, lang)}</p>
                    {r.cons && tr(r.cons, lang) && (
                      <p className="mt-2 flex flex-wrap gap-1.5 text-sm text-petrol/55">
                        <span className="font-semibold text-terracotta">− {t("detail.review_cons")}:</span>
                        {tr(r.cons, lang)}
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
